import { useQuery } from '@tanstack/react-query'
import { isAwake as checkAwake } from '../../hooks/useAwakePersonas'
import { Link } from 'react-router-dom'
import { useSSEConnected } from '../../hooks/useSSE'
import { fetchPersonaState, fetchConversations, fetchProjects, spawnPersona, popoutPersona } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import { timeAgo } from '../../hooks/useTimeAgo'
import type { PersonaState, Conversation } from '../../lib/types'

interface AgoraPost {
  id: string
  title: string
  author: string
  space: { slug: string; title: string; type: string; steward: string }
  vote_score: number
  comment_count: number
  created_at: string
  content?: string
}

interface AgoraSpace {
  slug: string
  title: string
  type: string
  steward: string
  post_count: number
  latest_post_at?: string
}

function AgoraWidget({ awakeSet }: { awakeSet: Set<string> }) {
  const { data: feedData } = useQuery<AgoraPost[]>({
    queryKey: ['agora-feed'],
    queryFn: async () => {
      const res = await fetch('/api/agora/feed')
      if (!res.ok) return []
      const d = await res.json()
      return Array.isArray(d) ? d : d.posts || d.feed || []
    },
    refetchInterval: 60_000,
  })

  const { data: spacesData } = useQuery<AgoraSpace[]>({
    queryKey: ['agora-spaces'],
    queryFn: async () => {
      const res = await fetch('/api/agora/spaces')
      if (!res.ok) return []
      const d = await res.json()
      return Array.isArray(d) ? d : d.spaces || []
    },
    refetchInterval: 120_000,
  })

  const posts = feedData || []
  const spaces = (spacesData || []).filter(s => s.post_count > 0).slice(0, 6)
  const trending = [...posts].sort((a, b) => (b.vote_score || 0) - (a.vote_score || 0)).slice(0, 3)

  if (posts.length === 0 && spaces.length === 0) return null

  return (
    <div
      className="rounded-lg p-4"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#4ECDC4' }}>
          Today in the Agora
        </h3>
        <Link to="/agora" className="text-xs no-underline" style={{ color: 'var(--text-muted)' }}>
          View all →
        </Link>
      </div>

      {trending.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-3">
          {trending.map(p => (
            <Link
              key={p.id}
              to={`/agora/${p.space?.slug || 'community'}/${p.id}`}
              className="block px-2.5 py-2 rounded transition-colors hover:bg-[var(--bg-elevated)] no-underline"
              style={{ border: '1px solid var(--border)' }}
            >
              <div className="flex items-start gap-2">
                <PersonaAvatar name={p.author} size={18} showStatus awake={checkAwake(awakeSet, p.author)} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {p.title}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                    {p.author} in {p.space?.title || '?'} · {timeAgo(p.created_at)}
                  </div>
                </div>
                {(p.vote_score > 0 || p.comment_count > 0) && (
                  <div className="text-xs shrink-0" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                    {p.vote_score > 0 && `↑${p.vote_score}`}
                    {p.comment_count > 0 && ` · ${p.comment_count}💬`}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {spaces.length > 0 && (
        <div>
          <div className="text-xs mb-1.5" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
            ACTIVE COMMUNITIES
          </div>
          <div className="flex flex-wrap gap-1">
            {spaces.map(s => (
              <Link
                key={s.slug}
                to={`/agora/${s.slug}`}
                className="text-xs px-2 py-1 rounded-full no-underline transition-colors"
                style={{
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  fontSize: 10,
                }}
              >
                {s.title} ({s.post_count})
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

type ExtPersona = PersonaState & Record<string, unknown>

function groupPersonas(state: Record<string, PersonaState>) {
  const needsYou: ExtPersona[] = []
  const awake: ExtPersona[] = []
  const sleeping: ExtPersona[] = []

  for (const [name, raw] of Object.entries(state)) {
    const p = { ...raw, name } as ExtPersona
    const hasNeeds = (p.needs_you_reasons as string[] || []).length > 0
    const hasQueue = (p.queue_count as number || 0) > 0

    if (p.awake) {
      if (hasNeeds) needsYou.push(p)
      else awake.push(p)
    } else {
      // Sleeping personas with queued messages also go to Needs You
      if (hasQueue) needsYou.push(p)
      else sleeping.push(p)
    }
  }
  return { needsYou, awake, sleeping }
}

function queueAge(p: ExtPersona): string {
  const msgs = p.queue_messages as { timestamp: string }[] | undefined
  if (!msgs?.length) return ''
  // Find oldest message
  const oldest = msgs.reduce((a, b) => (a.timestamp < b.timestamp ? a : b))
  return timeAgo(oldest.timestamp)
}

function PersonaCard({ p, onSpawn, onPopout }: {
  p: ExtPersona
  onSpawn: (name: string) => void
  onPopout: (name: string) => void
}) {
  const queueCount = (p.queue_count as number) || 0
  const role = p.role as string || ''

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[var(--bg-elevated)]"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <PersonaAvatar name={p.name} size={32} showStatus awake={p.awake} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
            {p.display_name || p.name}
          </span>
          {queueCount > 0 && (
            <span
              className="text-xs font-bold px-1.5 py-0.5 rounded-full leading-none"
              style={{ background: 'var(--warning)', color: '#000', fontSize: 10, minWidth: 16, textAlign: 'center' }}
            >
              {queueCount}
            </span>
          )}
        </div>
        {role && (
          <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {role}
          </div>
        )}
        {queueCount > 0 && (
          <div className="text-xs truncate" style={{ color: 'var(--warning)' }}>
            {queueCount} queued{queueAge(p) ? ` · oldest ${queueAge(p)}` : ''}
          </div>
        )}
        {!queueCount && p.status_line && p.status_line !== 'Sleeping' && (
          <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
            {p.status_line}
          </div>
        )}
      </div>
      {p.context_pct !== undefined && p.awake && (
        <span
          className="text-xs font-mono"
          style={{
            color: (p.context_pct as number) >= 75 ? 'var(--danger)' : (p.context_pct as number) >= 50 ? 'var(--warning)' : 'var(--text-muted)',
          }}
        >
          {p.context_pct}%
        </span>
      )}
      <div className="flex gap-1">
        {p.awake ? (
          <>
            <button
              onClick={() => onPopout(p.name)}
              className="text-xs px-2 py-1 rounded"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
              title="Open terminal tab"
            >
              Tab
            </button>
            <a
              href={`/terminal/${p.name}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs px-2 py-1 rounded no-underline"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
              title="iTerm popout"
            >
              iTerm
            </a>
          </>
        ) : (
          <button
            onClick={() => onSpawn(p.name)}
            className="text-xs px-2.5 py-1 rounded font-medium"
            style={{ background: 'var(--accent)', color: '#fff', border: 'none' }}
            title="Wake up"
          >
            Wake
          </button>
        )}
      </div>
    </div>
  )
}

function ConversationCard({ c, awakeSet }: { c: Conversation; awakeSet: Set<string> }) {
  return (
    <Link
      to={`/conversation/${c.id}`}
      className="block px-3 py-2.5 rounded-lg transition-colors hover:bg-[var(--bg-elevated)] no-underline"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="flex -space-x-1">
          {(c.participants || []).slice(0, 4).map(p => (
            <PersonaAvatar key={p.persona} name={p.persona} size={20} showStatus awake={checkAwake(awakeSet, p.persona)} />
          ))}
        </div>
        <span className="text-sm font-medium truncate flex-1" style={{ color: 'var(--text-primary)' }}>
          {c.title || c.id}
        </span>
        {c.last_message_at && (
          <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
            {timeAgo(c.last_message_at)}
          </span>
        )}
      </div>
      {c.last_message_preview && (
        <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
          {c.last_message_by && <span className="font-medium">{c.last_message_by}: </span>}
          {c.last_message_preview}
        </div>
      )}
    </Link>
  )
}

export default function LobbyPage() {
  const sseOk = useSSEConnected()
  const { data: stateData, isLoading: stateLoading } = useQuery({
    queryKey: ['personaState'],
    queryFn: fetchPersonaState,
    refetchInterval: sseOk ? false : 30_000, // SSE-conditional polling fallback
  })

  const { data: convoData, isLoading: convoLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => fetchConversations(20),
    refetchInterval: sseOk ? false : 30_000, // SSE-conditional polling fallback
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => fetchProjects('active'),
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const groups = stateData ? groupPersonas(stateData) : null
  const conversations = convoData?.conversations || []
  const awakeSet = new Set(
    stateData ? Object.entries(stateData).filter(([, s]) => s.awake).map(([n]) => n) : []
  )

  // Stats
  const allPersonas = stateData ? Object.values(stateData) : []
  const awakeCount = allPersonas.filter(p => p.awake).length
  const inConvoCount = allPersonas.filter(p => (p as ExtPersona).conversations?.length).length
  const activeProjects = Array.isArray(projectsData) ? projectsData.length : 0

  async function handleSpawn(name: string) {
    try { await spawnPersona(name) } catch (e) { console.error('Spawn failed:', e) }
  }

  async function handlePopout(name: string) {
    try { await popoutPersona(name) } catch (e) { console.error('Popout failed:', e) }
  }

  if (stateLoading || convoLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header with stats bar */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          The Gathering
        </h1>
        <div className="flex gap-4">
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-semibold" style={{ color: 'var(--success)' }}>{awakeCount}</span> awake
          </div>
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-semibold" style={{ color: 'var(--accent)' }}>{inConvoCount}</span> in conversation
          </div>
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{activeProjects}</span> projects
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — Personas */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Needs You — includes sleeping with queued messages */}
          {groups && groups.needsYou.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--warning)' }}>
                Needs You ({groups.needsYou.length})
              </h2>
              <div className="flex flex-col gap-1.5">
                {groups.needsYou.map(p => (
                  <PersonaCard key={p.name} p={p} onSpawn={handleSpawn} onPopout={handlePopout} />
                ))}
              </div>
            </section>
          )}

          {/* Awake */}
          {groups && groups.awake.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--success)' }}>
                Awake ({groups.awake.length})
              </h2>
              <div className="flex flex-col gap-1.5">
                {groups.awake.map(p => (
                  <PersonaCard key={p.name} p={p} onSpawn={handleSpawn} onPopout={handlePopout} />
                ))}
              </div>
            </section>
          )}

          {/* Sleeping */}
          {groups && groups.sleeping.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Sleeping ({groups.sleeping.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {groups.sleeping.map(p => (
                  <PersonaCard key={p.name} p={p} onSpawn={handleSpawn} onPopout={handlePopout} />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column — Agora + Conversations */}
        <div className="flex flex-col gap-4">
          <AgoraWidget awakeSet={awakeSet} />

          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-secondary)' }}>
              Active Conversations ({conversations.length})
            </h2>
            <div className="flex flex-col gap-1.5">
              {conversations.map(c => (
                <ConversationCard key={c.id} c={c} awakeSet={awakeSet} />
              ))}
              {conversations.length === 0 && (
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No active conversations</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
